// The move-input settings (move-input-spec §1): assistance, dragging, targets.
// In the trainer's toolbar behind a gear button; inline on the profile page.
import { useEffect, useRef, useState } from "react";
import { useLoggedIn, usePatchMe } from "../api/hooks";
import { useT } from "../i18n";
import { setPrefs, usePrefs, type Prefs } from "../lib/prefs";

type Key = "move_assist" | "move_drag" | "move_targets" | "auto_next";

export function MoveInputSettings({ inline = false }: { inline?: boolean }) {
  const t = useT();
  const prefs = usePrefs();
  const loggedIn = useLoggedIn();
  const patchMe = usePatchMe();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", close);
    return () => window.removeEventListener("pointerdown", close);
  }, [open]);

  function set<K extends Key>(k: K, v: Prefs[K]) {
    setPrefs({ [k]: v } as Partial<Prefs>);
    if (loggedIn) patchMe.mutate({ settings: { [k]: v } });
  }

  const controls = (
    <div className="stack" style={{ gap: 8 }}>
      <label>
        {t("move.assist")}
        <select value={prefs.move_assist} onChange={(e) => set("move_assist", e.target.value as Prefs["move_assist"])}>
          <option value="beginner">{t("move.beginner")}</option>
          <option value="advanced">{t("move.advanced")}</option>
        </select>
      </label>
      <label className="check">
        <input type="checkbox" checked={prefs.move_drag} onChange={(e) => set("move_drag", e.target.checked)} />
        {t("move.drag")}
      </label>
      <label className="check">
        <input type="checkbox" checked={prefs.move_targets} onChange={(e) => set("move_targets", e.target.checked)} />
        {t("move.targets")}
      </label>
      <label className="check">
        <input type="checkbox" checked={prefs.auto_next} onChange={(e) => set("auto_next", e.target.checked)} />
        {t("train.auto_next")}
      </label>
    </div>
  );

  if (inline) return controls;
  return (
    <div ref={box} style={{ position: "relative" }}>
      <button className="ghost" title={t("train.input")} aria-expanded={open} onClick={() => setOpen(!open)}>
        ⚙
      </button>
      {open && (
        <div className="card" role="dialog" aria-label={t("train.input")} style={{ position: "absolute", right: 0, top: "110%", zIndex: 20, width: 280 }}>
          {controls}
        </div>
      )}
    </div>
  );
}
